import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, LogIn } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuth";
import { Logo } from "../components/site/Logo";
import { Input } from "../components/ui/input";

export default function AdminLogin() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
  const { user, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/admin");
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-pblv-bg2 px-5 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-[2rem] border border-pblv-ink/10 bg-white p-8 shadow-[0_16px_50px_rgba(45,40,48,0.1)] md:p-10"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo />
          <span className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-pblv-mauve/12 text-pblv-mauve">
            <Lock className="h-5 w-5" strokeWidth={1.8} />
          </span>
          <h1 className="mt-4 font-serif text-3xl font-medium text-pblv-ink">
            Espace administrateur
          </h1>
          <p className="mt-2 text-sm text-pblv-inksoft">
            Gérez les T'POZ et les candidatures partenaires.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="email" className="text-sm font-medium text-pblv-ink">
              E-mail
            </label>
            <Input
              id="email"
              type="email"
              data-testid="admin-login-email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl border-pblv-ink/15 bg-pblv-bg px-4 py-3 focus-visible:ring-pblv-mauve"
              placeholder="admin@plusbellelavie971.com"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="password" className="text-sm font-medium text-pblv-ink">
              Mot de passe
            </label>
            <Input
              id="password"
              type="password"
              data-testid="admin-login-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl border-pblv-ink/15 bg-pblv-bg px-4 py-3 focus-visible:ring-pblv-mauve"
              placeholder="Votre mot de passe"
            />
          </div>

          {error && (
            <p
              data-testid="admin-login-error"
              className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-pblv-ink px-7 py-3.5 text-sm font-semibold text-pblv-bg transition-all duration-300 hover:-translate-y-0.5 hover:bg-pblv-mauve disabled:opacity-60"
          >
            <LogIn className="h-4 w-4" strokeWidth={1.8} />
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>

        <a
          href="/"
          className="mt-6 block text-center text-sm text-pblv-inksoft transition-colors hover:text-pblv-mauve"
        >
          ← Retour au site
        </a>
      </motion.div>
    </div>
  );
}
